import { motion } from 'framer-motion';
import {
  ArrowRight,
  Brain,
  Factory,
  Cog,
  TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export function ConsultingPreview() {
  const t = useT();
  const highlights = [
    {
      icon: Brain,
      title: t('KI-Projekte umsetzen', 'Deliver AI projects'),
      description: t(
        'Von der Idee zur produktiven Lösung. Praxisnah und ohne Berater-BlaBla.',
        'From idea to production-ready solution. Hands-on, with zero consultant jargon.',
      ),
    },
    {
      icon: Factory,
      title: t('20 Jahre Industriepraxis', '20 years of industry practice'),
      description: t(
        'Wir sind keine klassischen Berater. Wir sind Praktiker, die Klein- und Mittelständischen Unternehmen (KMU) helfen, Softwarelösungen umzusetzen, die sofort Nutzen bringen.',
        'We are not classic consultants. We are practitioners helping small and medium-sized enterprises (SMEs) implement software solutions that deliver value from day one.',
      ),
    },
    {
      icon: Cog,
      title: t('Individuelle Lösungen', 'Tailored solutions'),
      description: t(
        'Maßgeschneiderte Softwarelösungen die sofort einen Nutzen bringen.',
        'Custom-built software solutions that pay off immediately.',
      ),
    },
    {
      icon: TrendingUp,
      title: t('Messbare Ergebnisse', 'Measurable results'),
      description: t(
        'Kein Buzzword-Bingo. Wir optimieren dort, wo es wirklich zählt. Ehrlich. Direkt. Mit chirurgischer Präzision.',
        'No buzzword bingo. We optimize where it really matters. Honest. Direct. With surgical precision.',
      ),
    },
  ];

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center max-w-2xl mx-auto mb-14"
          {...fadeUp}
          transition={{ duration: 0.5 }}
        >
          <span className="badge-hero mb-4 inline-flex">
            <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
            {t('Consulting für KMU', 'Consulting for SMEs')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold mt-4">
            {t('Industrie-Know-how ', 'Industrial know-how ')}
            <span className="text-primary">{t('trifft KI', 'meets AI')}</span>
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            {t(
              'Wir sind keine klassischen Berater. Wir sind Praktiker, die Klein- und Mittelständischen Unternehmen (KMU) helfen, Softwarelösungen umzusetzen, die sofort Nutzen bringen.',
              'We are not classic consultants. We are practitioners helping SMEs implement software solutions that deliver value from day one.',
            )}
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {highlights.map((item, index) => (
            <motion.div
              key={item.title}
              className="group relative p-6 rounded-2xl bg-card border border-border/40 hover:border-primary/30 transition-all duration-300 hover:shadow-[0_0_30px_hsl(var(--primary)/0.1)]"
              {...fadeUp}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                <item.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          className="mt-12 text-center"
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Button variant="hero" size="lg" asChild>
            <LocalizedLink to="/consulting">
              {t('Mehr über unser Consulting', 'More about our consulting')}
              <ArrowRight className="w-5 h-5" />
            </LocalizedLink>
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
